import { useRef } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { fetchCasinoFinanceReport } from "./casinoFinanceReportApi";

const queryKeyBase = ["casino", "financeReport"];

/**
 * @param {{ start_date?: string; end_date?: string; limit?: number; page?: number; currentPage?: number; game?: string; subprovider?: string; money_type?: string[] }} params
 */
export function useCasinoFinanceReport(params, options = {}) {
  const {
    start_date,
    end_date,
    limit = 20,
    page: pageParam,
    currentPage: legacyCurrentPage,
    game,
    subprovider,
    money_type,
  } = params;
  const currentPage =
    Number(pageParam) > 0
      ? Number(pageParam)
      : Number(legacyCurrentPage) > 0
        ? Number(legacyCurrentPage)
        : 1;

  const pageCacheRef = useRef(new Map());
  const rangeKeyRef = useRef("");
  const moneyTypeKey = money_type?.join(",") ?? "";
  const rangeKey = `${start_date}|${end_date}|${limit}|${game ?? ""}|${subprovider ?? ""}|${moneyTypeKey}`;

  const query = useQuery({
    queryKey: [
      ...queryKeyBase,
      start_date,
      end_date,
      limit,
      currentPage,
      game,
      subprovider,
      moneyTypeKey,
    ],
    queryFn: async () => {
      // Clear before reading cache: React Query may run this for a new query key while
      // rangeKeyRef still holds the old value, so reset synchronously here.
      if (rangeKeyRef.current !== rangeKey) {
        pageCacheRef.current.clear();
        rangeKeyRef.current = rangeKey;
      }

      const cache = pageCacheRef.current;

      for (let p = 1; p <= currentPage; p += 1) {
        if (cache.has(p)) continue;

        const prev = p > 1 ? cache.get(p - 1) : undefined;
        const prevCursor = p > 1 ? prev?.nextCursor : undefined;
        try {
          const page = await fetchCasinoFinanceReport({
            start_date,
            end_date,
            limit,
            page: p,
            game,
            subprovider,
            money_type,
            ...(p > 1 && prevCursor != null && prevCursor !== ""
              ? { cursor: prevCursor }
              : {}),
          });
          cache.set(p, page);
        } catch (error) {
          // Some backend variants are strictly cursor-based after page 1.
          // If page > 1 has no cursor and request fails, preserve old UX: show an empty page.
          if (p > 1 && (prevCursor == null || prevCursor === "")) {
            const empty = {
              summary: cache.get(1)?.summary ?? null,
              rows: [],
              total: cache.get(1)?.total ?? 0,
              page: p,
              pageSize: limit,
              nextCursor: null,
            };
            cache.set(p, empty);
            continue;
          }
          throw error;
        }
      }

      const pageData = cache.get(currentPage);
      const first = cache.get(1);
      return {
        ...pageData,
        summary: pageData?.summary ?? first?.summary ?? null,
        total: pageData?.total ?? first?.total ?? pageData?.rows?.length ?? 0,
      };
    },
    enabled: options.enabled !== false && Boolean(start_date && end_date),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  return query;
}
